import { getTranscript } from "@husky-hrc/Framework/Handlers/Data/Videos/GetVideoCaptionsHandler";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography/Typography";
import { breakpointMediaQueries, getProp } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { css } from "styled-components";

const enum fields {
    youtubeId = "youtubeId",
    hasTranscript = "hasTranscript",
    fetchTranscript = "fetchTranscript",
    customTranscript = "customTranscript",
    thumbnail = "thumbnail",
    arrowColor = "arrowColor",
}

export interface VideoWidgetStyles {
    container?: GridContainerProps;
    videoWrapper?: InjectableCss;
    videoBlock?: GridItemProps;
    transcriptBlock?: InjectableCss;
    captionWrapper: InjectableCss;
    captionTitle: TypographyProps;
    captionYTText: TypographyProps;
    captionCustomText: TypographyProps;
    transcriptButton: InjectableCss;
    toggleLink?: InjectableCss;
    videoPreview?: InjectableCss;
    videoPreviewInnerContainer?: InjectableCss;
    videoPreviewButton?: InjectableCss;
}

export const videoWidgetStyles: VideoWidgetStyles = {
    container: {
        gap: 10,
        css: css`
            padding: 20px;
            margin-bottom: 32px;
        `,
    },
    videoWrapper: {
        css: css`
            position: relative;
            display: flex;
            justify-content: center;
            align-items: center;
            width: 100%;
            padding-bottom: 56.25%;
            overflow: hidden;
        `,
    },
    videoBlock: { width: 12 },
    transcriptBlock: {
        css: css`
            background-color: ${getColor("common.background")};
            padding: 1rem;
            margin: 5px !important;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        css`
                            flex-direction: column;
                        `,
                        null,
                        null,
                        css`
                            flex-direction: row;
                        `,
                        null,
                    ],
                    "min",
                );
            }}
        `,
    },
    captionWrapper: {
        css: css`
            padding-left: 1em;
            width: 100%;
            border-left: 4px solid ${getColor("common.border")};
        `,
    },
    captionTitle: {
        variant: "h3",
        as: "h3",
    },
    captionYTText: {
        size: "1.15em",
        css: css`
            display: block;
            width: 100%;
            margin-top: 1rem;
        `,
    },
    captionCustomText: {
        size: "1.15em",
        css: css`
            display: block;
            width: 100%;
            margin-top: 1rem;
            white-space: pre-line;
        `,
    },
    transcriptButton: {
        css: css`
            padding: 2em 0;
            border-top: 1px solid ${getColor("common.border")};
            margin-top: 2em;
        `,
    },
    toggleLink: {
        css: css`
            justify-self: flex-end;
            align-self: flex-start;
            text-align: right;
        `,
    },
    videoPreview: {
        css: css`
            background-image: url(${getProp("imageUrl")});
            width: 100%;
            position: relative;
            padding-bottom: 56.25%;
            background-size: cover;
            background-position: center;
            background-repeat: no-repeat;
            cursor: pointer;
        `,
    },
    videoPreviewInnerContainer: {
        css: css`
            position: absolute;
            width: 100%;
            height: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
        `,
    },
    videoPreviewButton: {
        css: css`
            width: 0;
            height: 0;
            border-top: 25px solid transparent;
            border-bottom: 25px solid transparent;
            border-left: 50px solid ${getProp("playButtonColor")};
        `,
    },
};

interface Props extends WidgetProps {
    fields: {
        [fields.youtubeId]: string;
        [fields.hasTranscript]: boolean;
        [fields.fetchTranscript]: boolean;
        [fields.customTranscript]: string;
        [fields.thumbnail]: string;
        [fields.arrowColor]: string;
    };
    language: string;
}

interface VideoPreviewProps {
    imageUrl: string;
    playButtonColor: string;
    onClick: () => void;
}

interface VideoTranscriptsProps {
    videoId: string;
    fetchTranscript: boolean;
    customTranscript?: string;
}

const parseYoutubeId = (urlField: string) => {
    const regExp = /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;
    const match = urlField.match(regExp);
    return match && match[7].length === 11 ? match[7] : urlField.length === 11 ? urlField : "";
};

const styles = videoWidgetStyles;

const VideoWidget = ({ fields, language }: Props) => {
    const hasThumbnail = !!fields.thumbnail;
    const [showPreview, setShowPreview] = useState(hasThumbnail);

    // Allows thumbnail preview toggle in the shell
    useEffect(() => {
        setShowPreview(hasThumbnail);
    }, [fields.thumbnail]);

    const videoId = parseYoutubeId(fields.youtubeId);
    const videoEmbedOptions = `?cc_load_policy=1&modestbranding=1&rel=0&autoplay=${hasThumbnail ? "1" : "0"}`;

    return (
        <GridContainer {...styles.container}>
            <GridItem {...styles.videoBlock}>
                {showPreview ? (
                    <VideoPreview
                        imageUrl={fields.thumbnail}
                        playButtonColor={fields.arrowColor ? fields.arrowColor : "#ffffff"}
                        onClick={() => {
                            setShowPreview(false);
                        }}
                    />
                ) : (
                    <StyledWrapper {...styles.videoWrapper}>
                        <iframe
                            src={`https://www.youtube.com/embed/${videoId}${videoEmbedOptions}`}
                            title=""
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                            style={{ position: "absolute", top: "0", left: "0", width: "100%", height: "100%" }}
                        ></iframe>
                    </StyledWrapper>
                )}
            </GridItem>
            {fields.hasTranscript && (fields.fetchTranscript || fields.customTranscript.length > 0) && (
                <GridItem {...styles.transcriptBlock} width={12}>
                    <TranscriptText
                        videoId={videoId}
                        fetchTranscript={fields.fetchTranscript}
                        customTranscript={fields.customTranscript}
                    />
                </GridItem>
            )}
        </GridContainer>
    );
};

const TranscriptText = ({ videoId, fetchTranscript, customTranscript }: VideoTranscriptsProps) => {
    const [hidden, setHidden] = useState(false);
    const [transcript, setTranscript] = useState([] as any);
    const language = useSelector<ApplicationState, string>(state => state.context.session.language!.languageCode);

    const toggleTranscriptDisplay = () => {
        setHidden(!hidden);
        if (fetchTranscript && transcript.length === 0) {
            fetchYouTubeCaptions();
        }
    };

    const fetchYouTubeCaptions = async () => {
        const lang = language.split("-")[0] || "en";
        const fetchedCaptions = await getTranscript(videoId, lang);
        const youTubeCaption = [] as any;

        fetchedCaptions.length > 0
            ? fetchedCaptions?.forEach((line: any, index: number) => {
                  youTubeCaption.push(
                      <Typography {...styles.captionYTText} key={index}>
                          ({line.start}) {line.text}
                      </Typography>,
                  );
              })
            : youTubeCaption.push(customCaption);

        setTranscript(youTubeCaption);
    };

    const customCaption = (
        <Typography {...styles.captionCustomText} key={customTranscript}>
            {customTranscript}
        </Typography>
    );

    const transcriptLink = (
        <Link onClick={toggleTranscriptDisplay}>
            {!hidden && translate("Read the Transcript")}
            {hidden && translate("Hide the Transcript")}
        </Link>
    );

    const displayTranscript = fetchTranscript || transcript.length > 0 ? true : (customTranscript?.length ?? 0) > 0;

    if (!displayTranscript) {
        return null;
    }

    return (
        <>
            <GridItem {...styles.toggleLink} width={[12, 12, 12, 2, 2]}>
                {transcriptLink}
            </GridItem>
            {hidden && (
                <GridItem width={[12, 12, 12, 10, 10]}>
                    <StyledWrapper {...styles.captionWrapper}>
                        <Typography {...styles.captionTitle}>{translate("Transcript")}</Typography>
                        {fetchTranscript ? (
                            transcript.length > 0 ? (
                                transcript
                            ) : (
                                <Typography size="1.15em">{translate("Loading...")}</Typography>
                            )
                        ) : (
                            <Typography {...styles.captionCustomText} key={customTranscript}>
                                {customTranscript}
                            </Typography>
                        )}
                        <StyledWrapper {...styles.transcriptButton}>{transcriptLink}</StyledWrapper>
                    </StyledWrapper>
                </GridItem>
            )}
        </>
    );
};

const VideoPreview = (props: VideoPreviewProps) => {
    return (
        <StyledWrapper {...props} {...styles.videoPreview} onClick={props.onClick}>
            <StyledWrapper {...styles.videoPreviewInnerContainer}>
                <StyledWrapper {...props} {...styles.videoPreviewButton}></StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const definition: WidgetDefinition = {
    group: "Basic",
    displayName: "Embed Video",
    icon: "Video",
    fieldDefinitions: [
        {
            name: fields.youtubeId,
            displayName: "YouTube Shareable URL",
            editorTemplate: "TextField",
            defaultValue: "",
            fieldType: "Translatable",
            placeholder: "YouTube video ID",
            isRequired: true,
        },
        {
            name: fields.thumbnail,
            displayName: "Thumbnail Image",
            fieldType: "Translatable",
            editorTemplate: "ImagePickerField",
            defaultValue: "",
            tooltip:
                "Optional: Display an overlay thumbnail over the embeded video. If not specified, the default YouTube thumbnail will be used. Use a 16:9 aspect ratio image if possible. Ideal dimensions should be around 1920px by 1080px.",
        },
        {
            name: fields.arrowColor,
            displayName: "Thumbnail Image Play Button Colour",
            fieldType: "Translatable",
            editorTemplate: "ColorPickerField",
            defaultValue: "white",
            tooltip:
                "Optional: default is white but another colour can be used for better contrast depending on the image used.",
        },
        {
            name: fields.hasTranscript,
            displayName: "Has Transcript?",
            editorTemplate: "CheckboxField",
            defaultValue: false,
            fieldType: "Translatable",
            tooltip: "Does it have a transcript, for accessibility purposes?",
        },
        {
            name: fields.customTranscript,
            displayName: "Custom Transcript",
            editorTemplate: "MultilineTextField",
            defaultValue: "",
            fieldType: "Translatable",
            tooltip: "Custom Transcript. Displayed on the page, beneath the video.",
        },
        {
            name: fields.fetchTranscript,
            displayName: "Fetch Transcript from YouTube?",
            editorTemplate: "CheckboxField",
            defaultValue: false,
            fieldType: "Translatable",
            tooltip: "Will try and fetch the Closed Caption from the YouTube video, matching the site language.",
        },
    ],
};

const widgetModule: WidgetModule = {
    component: VideoWidget,
    definition,
};

export default widgetModule;
